import { useCallback, useEffect, useRef, useState } from "react";

import { addDays, format, roundToNearestMinutes } from "date-fns";

import { useLazyGetStaffAvailabilityQuery } from "services/staff-availability";
import type { AppointmentType } from "services/types/appointment-types.type";
import type { Clinic } from "services/types/clinics.type";
import type {
  StaffAvailabilityFilters,
  StaffMemberAvailability
} from "services/types/staff-availability.type";
import type { MinimalStaffMember } from "services/types/staff.type";
import { strToDuration } from "utils/common";

// The backend hard-caps availability requests at 31 days
// (STAFF_AVAILABILITY_FILTER_MAX_RANGE_DAYS, ENG-1075 rollback) — never request more.
export const SEARCH_WINDOW_DAYS = 31;
// 3 windows ≈ 93 days, matching the web scheduler's bookable horizon.
export const MAX_SEARCH_WINDOWS = 3;
export const SLOTS_FETCH_LIMIT = 300;

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const FROM_TO_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";
const DEFAULT_SLOT_DURATION_MINUTES = 60;

export type SlotSource = "dedicated" | "open";

export interface FoundSlot {
  startTime: string;
  endTime: string;
  staffMember: MinimalStaffMember;
  clinic: Clinic;
  // "dedicated" = came from the type's appointment blocks; "open" = open
  // working-hours fallback for a provider with no blocks; null = the type
  // doesn't use blocks, so the distinction doesn't apply.
  source: SlotSource | null;
}

export interface SearchCriteria {
  appointmentType: AppointmentType;
  staffMemberIds: number[];
  clinicId: number;
  // Earliest moment to search from. The backend accepts any future
  // from_moment (it only clamps past dates to now — see
  // StaffAvailabilityQueryParametersSerializer), so a 3/6/12-month follow-up
  // search just starts its windows here. Absent/past values fall back to now.
  startFrom?: Date | null;
}

export type SearchStatus = "idle" | "searching" | "extending" | "done";

export function getSlotDurationMinutes(
  appointmentType: AppointmentType | null | undefined
): number {
  if (!appointmentType?.default_duration) return DEFAULT_SLOT_DURATION_MINUTES;
  const { hours = 0, minutes = 0 } = strToDuration(
    appointmentType.default_duration
  );
  const total = (hours || 0) * 60 + (minutes || 0);
  return total > 0 ? total : DEFAULT_SLOT_DURATION_MINUTES;
}

function toFoundSlots(
  response: StaffMemberAvailability[],
  source: SlotSource | null
): FoundSlot[] {
  return response.map((slot) => ({
    startTime: slot.start_moment,
    endTime: slot.end_moment,
    staffMember: slot.staff_member,
    clinic: slot.clinic,
    source
  }));
}

const byStartTimeAsc = (a: FoundSlot, b: FoundSlot) =>
  new Date(a.startTime).getTime() - new Date(b.startTime).getTime();

/**
 * Progressive "next available slot" search across up to MAX_SEARCH_WINDOWS
 * consecutive 31-day windows.
 *
 * Window 1 always runs for every requested provider; windows 2..N run only for
 * providers that still have zero slots, and the loop stops as soon as everyone
 * has at least one. This keeps the common case at one round trip while never
 * reporting a false "no availability" inside the ~93-day horizon.
 *
 * Per window, when the appointment type is limited to appointment blocks, a
 * dedicated-blocks query runs first and providers with no block slots in that
 * window automatically fall back to an open-time query (the agreed
 * auto-fallback semantics). Types without blocks issue a single open-time
 * query — the backend still keeps other types' reserved blocks out of it.
 *
 * Slots accumulate into state after every window (sorted by start), so the UI
 * paints window-1 results immediately while later windows are still loading
 * (`status === "extending"`).
 */
export default function useFindNextAvailable() {
  const [fetchAvailability] = useLazyGetStaffAvailabilityQuery();

  const [slots, setSlots] = useState<FoundSlot[]>([]);
  const [status, setStatus] = useState<SearchStatus>("idle");
  const [hasError, setHasError] = useState(false);
  // Providers that ended the whole search with zero slots.
  const [staffIdsWithoutSlots, setStaffIdsWithoutSlots] = useState<number[]>(
    []
  );
  // Providers the in-flight extension windows are still looking for.
  const [staffIdsStillSearching, setStaffIdsStillSearching] = useState<
    number[]
  >([]);
  const [searchedThroughDate, setSearchedThroughDate] = useState<Date | null>(
    null
  );

  // Monotonic token: bumping it makes any in-flight search's late responses
  // no-ops instead of stomping the next search's state.
  const searchIdRef = useRef(0);

  useEffect(
    () => () => {
      searchIdRef.current += 1;
    },
    []
  );

  const reset = useCallback(() => {
    searchIdRef.current += 1;
    setSlots([]);
    setStatus("idle");
    setHasError(false);
    setStaffIdsWithoutSlots([]);
    setStaffIdsStillSearching([]);
    setSearchedThroughDate(null);
  }, []);

  const fetchWindow = useCallback(
    async (
      criteria: SearchCriteria,
      staffIds: number[],
      fromDate: string,
      toDate: string
    ): Promise<FoundSlot[]> => {
      const baseFilters: Omit<StaffAvailabilityFilters, "staffMembersIds"> = {
        currentClinicId: criteria.clinicId,
        appointmentTypeId: criteria.appointmentType.id,
        slotDurationMinutes: getSlotDurationMinutes(criteria.appointmentType),
        excludePersonalEvents: true,
        fromDate,
        toDate,
        limit: SLOTS_FETCH_LIMIT,
        // The response is one page of `limit` slots sorted by start time, so
        // one wide-open provider can fill the whole page within days and make
        // an off-those-days provider look slot-less for the window. Capping
        // per provider guarantees everyone fits under the page limit.
        limitPerStaffMember: Math.floor(SLOTS_FETCH_LIMIT / staffIds.length)
      };

      if (!criteria.appointmentType.limit_to_appointment_blocks) {
        const openTime = await fetchAvailability(
          { ...baseFilters, staffMembersIds: staffIds },
          true
        ).unwrap();
        return toFoundSlots(openTime, null);
      }

      const dedicated = await fetchAvailability(
        {
          ...baseFilters,
          staffMembersIds: staffIds,
          limitToAppointmentBlocks: true
        },
        true
      ).unwrap();
      const dedicatedSlots = toFoundSlots(dedicated, "dedicated");

      const staffIdsWithBlocks = new Set(
        dedicatedSlots.map((slot) => slot.staffMember?.id)
      );
      const fallbackStaffIds = staffIds.filter(
        (id) => !staffIdsWithBlocks.has(id)
      );
      if (fallbackStaffIds.length === 0) return dedicatedSlots;

      const openTime = await fetchAvailability(
        { ...baseFilters, staffMembersIds: fallbackStaffIds },
        true
      ).unwrap();
      return [...dedicatedSlots, ...toFoundSlots(openTime, "open")];
    },
    [fetchAvailability]
  );

  const search = useCallback(
    async (criteria: SearchCriteria) => {
      const searchId = ++searchIdRef.current;

      setSlots([]);
      setHasError(false);
      setStaffIdsWithoutSlots([]);
      setStaffIdsStillSearching([]);
      setStatus("searching");

      // The backend clamps from_moment to now for past dates — start at a
      // ceil-rounded now, matching the in-form availability panel. A future
      // startFrom (the "Start search from" presets) wins over now.
      const roundedNow = roundToNearestMinutes(new Date(), {
        nearestTo: 30,
        roundingMethod: "ceil"
      });
      const searchStart =
        criteria.startFrom &&
        criteria.startFrom.getTime() > roundedNow.getTime()
          ? criteria.startFrom
          : roundedNow;

      const foundSlots: FoundSlot[] = [];
      // Defensive: windows are [from, to) so adjacent windows shouldn't
      // overlap, but a duplicate (staff, start) must never render twice.
      const seenSlotKeys = new Set<string>();
      let remainingStaffIds = [...criteria.staffMemberIds];

      try {
        let windowStart = searchStart;
        for (
          let windowIndex = 0;
          windowIndex < MAX_SEARCH_WINDOWS && remainingStaffIds.length > 0;
          windowIndex += 1
        ) {
          // addDays keeps wall-clock time, so a window spanning a DST
          // fall-back is 31 days + 1 hour of absolute time — over the BE's
          // strict `(to_moment - from_moment) > 31 days` validation (400).
          // Cap the end at 31 absolute days too; the next window starts at
          // this end, so no boundary hour is ever skipped.
          const windowEnd = new Date(
            Math.min(
              addDays(windowStart, SEARCH_WINDOW_DAYS).getTime(),
              windowStart.getTime() + SEARCH_WINDOW_DAYS * MS_PER_DAY
            )
          );

          const windowSlots = await fetchWindow(
            criteria,
            remainingStaffIds,
            format(windowStart, FROM_TO_FORMAT),
            format(windowEnd, FROM_TO_FORMAT)
          );
          if (searchId !== searchIdRef.current) return;

          const freshSlots = windowSlots.filter((slot) => {
            const key = `${slot.staffMember?.id}|${slot.startTime}`;
            if (seenSlotKeys.has(key)) return false;
            seenSlotKeys.add(key);
            return true;
          });
          foundSlots.push(...freshSlots);
          const staffIdsWithSlots = new Set(
            foundSlots.map((slot) => slot.staffMember?.id)
          );
          remainingStaffIds = remainingStaffIds.filter(
            (id) => !staffIdsWithSlots.has(id)
          );

          setSlots([...foundSlots].sort(byStartTimeAsc));
          setSearchedThroughDate(windowEnd);
          if (remainingStaffIds.length > 0) {
            setStaffIdsStillSearching(remainingStaffIds);
            setStatus("extending");
          }
          windowStart = windowEnd;
        }

        if (searchId !== searchIdRef.current) return;
        setStaffIdsStillSearching([]);
        setStaffIdsWithoutSlots(remainingStaffIds);
        setStatus("done");
      } catch {
        if (searchId !== searchIdRef.current) return;
        setHasError(true);
        setStaffIdsStillSearching([]);
        setStatus("done");
      }
    },
    [fetchWindow]
  );

  return {
    search,
    reset,
    slots,
    status,
    hasError,
    staffIdsWithoutSlots,
    staffIdsStillSearching,
    searchedThroughDate
  };
}
